import { Directive, Input, OnChanges, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionService } from '../../services/permission.service';

type PermissionInput = string | string[] | null | undefined;
type PermissionMode = 'any' | 'all';

/**
 * Diretiva estrutural que exibe/oculta elementos com base nas permissões do usuário.
 *
 * Uso básico (uma permissão):
 *   <button *appHasPermission="'CreateVehicle'">Criar</button>
 *
 * Uso com lista (mode "any" — padrão):
 *   <div *appHasPermission="['CreateVehicle', 'EditVehicle']">...</div>
 *
 * Uso com mode "all" (todas as permissões necessárias):
 *   <div *appHasPermission="['ApproveRental', 'EditRental']; mode: 'all'">...</div>
 *
 * Sintaxe alternativa (attribute-style):
 *   <button [appHasPermission]="'DeleteVehicle'" [appHasPermissionMode]="'any'">Excluir</button>
 */
@Directive({
  selector: '[appHasPermission]'
})
export class HasPermissionDirective implements OnChanges, OnDestroy {
  @Input('appHasPermission') requiredPermission: PermissionInput = null;
  @Input() appHasPermissionMode: PermissionMode = 'any';

  private hasView = false;
  private permissionsSub?: Subscription;

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainer: ViewContainerRef,
    private permissionService: PermissionService
  ) {
    this.permissionsSub = this.permissionService.userPermissions$.subscribe(() => {
      this.updateView();
    });
  }

  ngOnChanges(): void {
    this.updateView();
  }

  private updateView(): void {
    const required = this.requiredPermission;

    if (required === null || required === undefined) {
      this.show();
      return;
    }

    const requiredList = Array.isArray(required) ? required : [required];

    const hasAccess =
      this.appHasPermissionMode === 'all'
        ? this.permissionService.hasAllPermissions(requiredList)
        : this.permissionService.hasAnyPermission(requiredList);

    hasAccess ? this.show() : this.hide();
  }

  private show(): void {
    if (this.hasView) return;
    this.viewContainer.createEmbeddedView(this.templateRef);
    this.hasView = true;
  }

  private hide(): void {
    if (!this.hasView) return;
    this.viewContainer.clear();
    this.hasView = false;
  }

  ngOnDestroy(): void {
    this.permissionsSub?.unsubscribe();
  }
}
